import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PartyPopper } from 'lucide-react';
import { ActionRequiredBanner } from '@/components/domain/shared/ActionRequiredBanner';
import { TaskCard } from '@/components/domain/shared/TaskCard';
import { EventCard } from '@/components/domain/shared/EventCard';
import { MobileSuggestionModal } from '@/components/domain/shared/MobileSuggestionModal';
import { AhaWalkthroughModal } from '@/components/domain/shared/AhaWalkthrough';
import { ChildFilterPills } from '@/components/Layout/mobile/ChildFilterPills';
import { HamburgerMenuButton } from '@/components/Layout/mobile/HamburgerMenuButton';
import { TodayTabSkeleton } from '@/components/states/PageSkeletons';
import { ErrorState } from '@/components/states/ErrorState';
import { CollapsibleSection } from '@/components/ui';
import { useAppShell } from '@/hooks/useAppShell';
import { useHeroScrolled } from '@/hooks/useHeroScrolled';
import { useTaskCompletion } from '@/hooks/useTaskCompletion';
import { useChildList } from '@/hooks/useChildList';
import {
  useAhaWalkthrough,
  useFeedbackPrompt,
  useIsMobile,
  usePermissions,
} from '@/hooks';
import { FeedbackModal } from '@/components/domain/shared/FeedbackModal';
import { scheduleService } from '@/services/schedule.service';
import { buildSeriesDelete } from '@/lib/seriesDelete';
import { seriesLabel } from '@/lib/recurrence';
import { getChildren, getMembers } from '@/services/household.service';
import type { HouseholdMemberWithUser } from '@/types';
import { useUserStore } from '@/stores/userStore';
import { getTodayKey, toIsoWithTz } from '@/lib/tz';
import {
  getPendingReviewItemCount,
  getReviewCount,
} from '@/services/email.service';
import {
  buildUnifiedFeed,
  partitionTodayTasks,
  taskHasChild,
  eventHasChild,
  getChildrenFromTask,
  getChildrenFromEvent,
  getMembersFromTask,
  getMembersFromEvent,
  getSourceSubject,
  taskToReviewItem,
  eventToReviewItem,
  type TaskRow,
  type EventRow,
} from '@/lib/schedule';
import { ItemFormModal, type ItemFormData } from '@/pages/review/ItemFormModal';
import { TaskEventDetailModal } from '@/components/domain/shared/TaskEventDetailModal';
import type { Child } from '@/types';

const DAY_NAMES_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

// A greeting is first-name territory: "Good morning, Ana", not "Good morning,
// Ana Dawson". The full name still belongs in the sidebar, so this trims here
// rather than narrowing what AppShell hands down. With no name at all the
// comma goes too, so an empty profile does not render "Good morning, ".
function getGreeting(name?: string): string {
  const hour = new Date().getHours();
  const firstName = name?.trim().split(/\s+/)[0] ?? '';
  const suffix = firstName ? `, ${firstName}` : '';
  if (hour < 12) return `Good morning${suffix}`;
  if (hour < 17) return `Good afternoon${suffix}`;
  return `Good evening${suffix}`;
}

const MOBILE_SUGGESTION_SEEN_KEY = 'yaruta_mobile_suggestion_seen';

export function TodayTab() {
  const navigate = useNavigate();
  const context = useAppShell();
  const { householdId } = context;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [tasks, setTasks] = useState<TaskRow[]>([]);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [reviewCount, setReviewCount] = useState(0);
  const [pendingItemCount, setPendingItemCount] = useState(0);
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);
  const [householdChildren, setHouseholdChildren] = useState<Child[]>([]);
  const [householdMembers, setHouseholdMembers] = useState<
    HouseholdMemberWithUser[]
  >([]);
  const [editingItem, setEditingItem] = useState<
    { kind: 'task'; data: TaskRow } | { kind: 'event'; data: EventRow } | null
  >(null);
  const [detailItem, setDetailItem] = useState<
    { kind: 'task'; data: TaskRow } | { kind: 'event'; data: EventRow } | null
  >(null);
  const [heroRef, isScrolled] = useHeroScrolled();

  // Desktop-only mobile-suggestion modal
  const isMobile = useIsMobile();
  const [showMobileModal, setShowMobileModal] = useState(false);

  useEffect(() => {
    if (isMobile) return;
    if (localStorage.getItem(MOBILE_SUGGESTION_SEEN_KEY)) return;
    const timer = setTimeout(() => setShowMobileModal(true), 600);
    return () => clearTimeout(timer);
  }, [isMobile]);

  const handleCloseMobileModal = useCallback(() => {
    localStorage.setItem(MOBILE_SUGGESTION_SEEN_KEY, '1');
    setShowMobileModal(false);
  }, []);

  const userTimezone = useUserStore((s) => s.timezone);
  const { canEdit, canComplete, canDelete, canManageMembers } =
    usePermissions();

  // YAR-238 walkthrough trigger. Auto-shows on the first Today visit
  // after Gmail-connected onboarding. Admin-only (canManageMembers) —
  // the carousel step 1 edits approved senders, which is an
  // admin-only mutation (same surface as Gmail/member management).
  const aha = useAhaWalkthrough({ householdId });
  const [walkthroughOpen, setWalkthroughOpen] = useState(false);
  // Auto-open at most once per mount. TodayTab's `aha` instance is
  // separate from the modal's, so a dismiss never clears this instance's
  // stale `shouldAutoShow`. Without this guard, any later
  // `shouldAutoShow` false→true re-transition (a household switch or a
  // Gmail connection re-settle `connected→undefined→connected`) would
  // re-fire this effect and resurrect a walkthrough the user already
  // dismissed (AC 4). A reload/tab-return is unaffected — it remounts and
  // re-reads the persisted terminal state.
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (aha.shouldAutoShow && canManageMembers && !autoOpenedRef.current) {
      autoOpenedRef.current = true;
      setWalkthroughOpen(true);
    }
  }, [aha.shouldAutoShow, canManageMembers]);

  // One-time feedback nudge once the household has approved 5+ emails.
  // Gated so it never competes with the aha walkthrough auto-trigger.
  const feedback = useFeedbackPrompt({
    householdId,
    enabled: !walkthroughOpen && !(aha.shouldAutoShow && canManageMembers),
  });
  const today = getTodayKey(userTimezone);
  const now = new Date();
  const dateLabel = `${DAY_NAMES_SHORT[now.getDay()]}, ${MONTH_NAMES_SHORT[now.getMonth()]} ${now.getDate()}`;
  const greeting = getGreeting(context?.userName);

  // Only the newest loadData may write state. Loads overlap (a mount load
  // and a silent reload after completing a task, or two quick completions),
  // and an older response landing last would put back a task the parent
  // just completed. Same pattern as ScheduleTab's reloadSubtasks.
  const loadSeqRef = useRef(0);
  const loadData = useCallback(
    async (fId: string, opts: { silent?: boolean } = {}) => {
      const seq = ++loadSeqRef.current;
      try {
        // A silent reload follows a mutation the page already shows
        // optimistically (a completed task left the list): swapping the
        // feed for a skeleton would flash the row it just removed.
        if (!opts.silent) setLoading(true);
        setError(null);
        const [taskData, eventData, count, items] = await Promise.all([
          scheduleService.getTodayTasks(fId, userTimezone),
          scheduleService.getTodayEvents(fId, userTimezone),
          getReviewCount(fId),
          // Decorative line on the card: never fail the page over it.
          getPendingReviewItemCount(fId).catch(() => 0),
        ]);
        if (seq !== loadSeqRef.current) return;
        setTasks(taskData as TaskRow[]);
        setEvents(eventData as EventRow[]);
        setReviewCount(count);
        setPendingItemCount(items);
      } catch (err) {
        if (seq !== loadSeqRef.current) return;
        console.error('Failed to load today data:', err);
        setError(
          err instanceof Error
            ? err
            : new Error("Unable to load today's items"),
        );
      } finally {
        // A superseded load leaves the skeleton to the newer one.
        if (seq === loadSeqRef.current) setLoading(false);
      }
    },
    [userTimezone],
  );

  useEffect(() => {
    if (!householdId) {
      // Only stop showing the skeleton once resolution has FINISHED.
      // Otherwise the first paint (id still pending) renders this page's
      // empty/default state before the real data can possibly arrive.
      if (context.householdResolved) setLoading(false);
      return;
    }
    let cancelled = false;
    // Fire both at once. The roster needs nothing but householdId, which
    // is already in scope, so queueing it behind the day's data delayed it
    // for no reason (ScheduleTab already loads it concurrently).
    void loadData(householdId);
    // Deliberately NOT part of the skeleton gate: loadData owns
    // setLoading, and a roster failure stays non-fatal, exactly as before.
    void (async () => {
      try {
        const [kids, members] = await Promise.all([
          getChildren(householdId),
          getMembers(householdId),
        ]);
        if (!cancelled) {
          setHouseholdChildren(kids);
          setHouseholdMembers(members);
        }
      } catch (err) {
        console.error('Failed to load household children/members:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [householdId, context.householdResolved, loadData]);

  // Reload this page AND the app chrome. The nav's Today badge is fed by its
  // own query in AppShell, which cannot observe a mutation made here — so
  // without this the badge kept yesterday's number until the next boot, and
  // the AC is explicit that it decrements as items are completed or removed.
  // Mount loads deliberately call `loadData` directly: the badge already
  // counts on its own, and re-counting it on every visit to Today would be a
  // duplicate query for no change.
  const { refreshChrome, refreshKey, reviewCount: shellReviewCount } = context;

  // Keep the card's number in step with the nav Review badge (YAR-478):
  // re-read whenever the chrome re-counts (an email finished processing, an
  // approval landed) or its 30s poll moves the badge. Skips the mount run —
  // loadData already read both.
  const summarySeenRef = useRef<string | null>(null);
  useEffect(() => {
    if (!householdId) return;
    const key = `${householdId}:${refreshKey ?? 0}:${shellReviewCount ?? ''}`;
    if (summarySeenRef.current === null) {
      summarySeenRef.current = key;
      return;
    }
    if (summarySeenRef.current === key) return;
    summarySeenRef.current = key;
    let cancelled = false;
    void Promise.all([
      getReviewCount(householdId),
      getPendingReviewItemCount(householdId),
    ])
      .then(([count, items]) => {
        if (cancelled) return;
        setReviewCount(count);
        setPendingItemCount(items);
      })
      .catch(() => {
        /* keep the last numbers; the next re-count retries */
      });
    return () => {
      cancelled = true;
    };
  }, [householdId, refreshKey, shellReviewCount]);
  const reloadAll = useCallback(
    async (id: string) => {
      await loadData(id);
      refreshChrome?.();
    },
    [loadData, refreshChrome],
  );

  // Completing a task (YAR-476): it leaves the list at once, the undo toast
  // comes up, and Undo writes back the status it had. The re-read is silent
  // and re-counts the nav badge.
  const reloadAfterStatusChange = useCallback(async () => {
    if (householdId) await loadData(householdId, { silent: true });
    refreshChrome?.();
  }, [householdId, loadData, refreshChrome]);
  const completion = useTaskCompletion({
    onChanged: reloadAfterStatusChange,
  });
  const { applyOverrides } = completion;
  const visibleTasks = useMemo(
    () => applyOverrides(tasks),
    [applyOverrides, tasks],
  );

  const handleEditItem = useCallback(
    async (data: ItemFormData) => {
      if (!editingItem) return;
      const childIds = data.child_names
        .map((name) => householdChildren.find((c) => c.name === name)?.id)
        .filter((id): id is string => typeof id === 'string');
      const toIso = (dt: string) => toIsoWithTz(dt, userTimezone);

      if (editingItem.kind === 'task') {
        const raw = data.due_datetime ?? data.start_datetime ?? null;
        await scheduleService.updateTask(editingItem.data.id, {
          title: data.title,
          due_date: raw ? toIso(raw) : null,
          end_date: data.end_datetime ? toIso(data.end_datetime) : null,
          notes: data.description ?? null,
          location: data.location ?? null,
          links: data.links,
          child_ids: childIds,
          member_user_ids: data.member_user_ids,
          // YAR-309: the form has emitted this since YAR-295 wired up the
          // "Related Event" dropdown; every handleEditItem simply never
          // forwarded it, so clearing or changing the link was a no-op.
          related_event_id: data.related_event_id,
        });
      } else {
        await scheduleService.updateEvent(editingItem.data.id, {
          title: data.title,
          start_date: data.start_datetime
            ? toIso(data.start_datetime)
            : editingItem.data.start_date,
          end_date: data.end_datetime ? toIso(data.end_datetime) : null,
          location: data.location ?? null,
          links: data.links,
          notes: data.description ?? null,
          child_ids: childIds,
          member_user_ids: data.member_user_ids,
        });
      }

      setEditingItem(null);
      if (householdId) await reloadAll(householdId);
    },
    [editingItem, householdChildren, householdId, reloadAll, userTimezone],
  );

  const childList = useChildList(tasks, events);

  // Filter by selected child
  const filteredTasks = useMemo(
    () =>
      selectedChildId
        ? visibleTasks.filter((t) => taskHasChild(t, selectedChildId))
        : visibleTasks,
    [visibleTasks, selectedChildId],
  );

  const filteredEvents = useMemo(
    () =>
      selectedChildId
        ? events.filter((e) => eventHasChild(e, selectedChildId))
        : events,
    [events, selectedChildId],
  );

  // Split tasks into overdue / today / no-date. No-date open tasks land
  // in their own section below the dated feed so they stay discoverable
  // without crowding "Happening Today".
  const partition = useMemo(
    () => partitionTodayTasks(filteredTasks, today, userTimezone),
    [filteredTasks, today, userTimezone],
  );
  const overdueTasks = partition.overdue;
  const todayDatedTasks = partition.todayDated;
  const noDateTasks = partition.noDate;

  // Build unified feed from today's events + today's dated tasks. No-date
  // tasks are intentionally excluded — they render in their own section.
  const unifiedFeed = useMemo(
    () => buildUnifiedFeed(filteredEvents, todayDatedTasks),
    [filteredEvents, todayDatedTasks],
  );

  const hasContent =
    overdueTasks.length > 0 ||
    filteredEvents.length > 0 ||
    todayDatedTasks.length > 0 ||
    noDateTasks.length > 0;

  // Error state — Figma: only hamburger + centered error, no date/greeting
  if (error && !loading) {
    return (
      <div className="flex flex-col min-h-full bg-surface">
        <div className="p-5">
          {context?.onOpenDrawer && (
            <HamburgerMenuButton
              onClick={context.onOpenDrawer}
              showBadge={reviewCount > 0}
            />
          )}
        </div>
        <ErrorState
          message="Unable to load today's items"
          onRetry={() => {
            setError(null);
            if (householdId) loadData(householdId);
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-full bg-surface">
      {/* Fixed condensed header + pills — appears on scroll */}
      <div
        className={[
          'fixed top-0 left-0 right-0 z-[100] bg-background transition-all duration-200',
          isScrolled
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 -translate-y-full pointer-events-none',
        ].join(' ')}
      >
        <div className="flex items-center justify-center px-5 py-2">
          <div className="flex-1 min-w-0">
            {context?.onOpenDrawer && (
              <HamburgerMenuButton
                onClick={context.onOpenDrawer}
                showBadge={reviewCount > 0}
                variant="compact"
              />
            )}
          </div>
          <span className="text-xl font-bold leading-6 text-foreground text-center whitespace-nowrap">
            Today ({dateLabel})
          </span>
          <div className="flex-1 min-w-0" />
        </div>
        {childList.length > 0 && (
          <div className="px-5 pb-3">
            <ChildFilterPills
              childList={childList}
              selectedId={selectedChildId}
              onSelect={setSelectedChildId}
            />
          </div>
        )}
        <div className="h-px bg-border" />
      </div>

      {/* Scrollable content */}
      <div className="p-5 flex flex-col gap-5 pb-24">
        {/* Hamburger — default (visible before scroll) */}
        <div ref={heroRef}>
          {context?.onOpenDrawer && (
            <div className="mb-4">
              <HamburgerMenuButton
                onClick={context.onOpenDrawer}
                showBadge={reviewCount > 0}
              />
            </div>
          )}

          {/* Large date heading + greeting */}
          <h1 className="text-[40px] font-bold leading-[48px] text-foreground">
            {dateLabel}
          </h1>
          <p className="text-xl font-medium text-muted-foreground">
            {greeting}
          </p>
        </div>

        {/* Child filter pills — Figma: pills before banner */}
        {childList.length > 0 && (
          <ChildFilterPills
            childList={childList}
            selectedId={selectedChildId}
            onSelect={setSelectedChildId}
          />
        )}

        {/* Action required banner */}
        {reviewCount > 0 && (
          <ActionRequiredBanner
            // Approved copy (YAR-478): "[n] email(s)" — the same number as
            // the Review badge — and "Yaruta found [X] new items from
            // senders you approve", omitted when X is 0.
            title={`${reviewCount} ${reviewCount === 1 ? 'email' : 'emails'}`}
            description={
              pendingItemCount > 0
                ? `Yaruta found ${pendingItemCount} new ${
                    pendingItemCount === 1 ? 'item' : 'items'
                  } from senders you approve`
                : undefined
            }
            onAction={() => navigate('/review')}
          />
        )}

        {/* Loading state */}
        {loading && <TodayTabSkeleton />}

        {/* Overdue section */}
        {!loading && !error && overdueTasks.length > 0 && (
          <CollapsibleSection
            title="Overdue"
            count={overdueTasks.length}
            tone="danger"
          >
            <div className="flex flex-col gap-2">
              {overdueTasks.map((task) => (
                <TaskCard
                  key={task.id}
                  id={task.id}
                  title={task.title}
                  description={task.notes ?? undefined}
                  dueDate={task.due_date ?? undefined}
                  location={task.location ?? undefined}
                  link={task.link ?? undefined}
                  status={
                    task.status as 'TODO' | 'IN_PROGRESS' | 'DONE' | 'CANCELLED'
                  }
                  children={getChildrenFromTask(task).map((c) => ({
                    name: c.name,
                    emoji: c.emoji_avatar ?? undefined,
                    color: c.emoji_color ?? undefined,
                    imagePath: c.avatar_image_path ?? undefined,
                  }))}
                  members={getMembersFromTask(task).map((m) => ({
                    name: m.display_name ?? m.email ?? 'Member',
                    emoji: m.avatar_emoji ?? undefined,
                    color: m.avatar_color ?? undefined,
                    imagePath: m.avatar_image_path ?? undefined,
                  }))}
                  sourceSubject={getSourceSubject(task)}
                  isOverdue
                  onToggleComplete={
                    canComplete ? () => void completion.toggle(task) : undefined
                  }
                  onClick={() => setDetailItem({ kind: 'task', data: task })}
                  timezone={userTimezone}
                />
              ))}
            </div>
          </CollapsibleSection>
        )}

        {/* Happening Today — unified feed. Hidden at 0 like Overdue and
            Unscheduled: checking the last item used to leave an empty
            "Happening Today (0)" header with the all-cleared message stuck
            inside it (Kelvin, via QA on YAR-399, Sep 22). */}
        {!loading && !error && unifiedFeed.length > 0 && (
          <CollapsibleSection
            title="Happening Today"
            count={unifiedFeed.length}
            tone="neutral"
          >
            <div className="flex flex-col gap-2">
              {unifiedFeed.map((item) => {
                if (item.type === 'event') {
                  const event = item.data;
                  return (
                    <EventCard
                      key={event.id}
                      id={event.id}
                      title={event.title}
                      description={event.notes ?? undefined}
                      startDate={event.start_date}
                      endDate={event.end_date ?? undefined}
                      isTimeUnknown={
                        event.source_review_item?.is_time_unknown ?? false
                      }
                      location={event.location ?? undefined}
                      link={event.link ?? undefined}
                      children={getChildrenFromEvent(event).map((c) => ({
                        name: c.name,
                        emoji: c.emoji_avatar ?? undefined,
                        color: c.emoji_color ?? undefined,
                        imagePath: c.avatar_image_path ?? undefined,
                      }))}
                      members={getMembersFromEvent(event).map((m) => ({
                        name: m.display_name ?? m.email ?? 'Member',
                        emoji: m.avatar_emoji ?? undefined,
                        color: m.avatar_color ?? undefined,
                        imagePath: m.avatar_image_path ?? undefined,
                      }))}
                      sourceSubject={getSourceSubject(event)}
                      repeatLabel={seriesLabel(event) ?? undefined}
                      onClick={() =>
                        setDetailItem({ kind: 'event', data: event })
                      }
                      timezone={userTimezone}
                    />
                  );
                }
                const task = item.data;
                return (
                  <TaskCard
                    key={task.id}
                    id={task.id}
                    title={task.title}
                    description={task.notes ?? undefined}
                    dueDate={task.due_date ?? undefined}
                    location={task.location ?? undefined}
                    link={task.link ?? undefined}
                    status={
                      task.status as
                        | 'TODO'
                        | 'IN_PROGRESS'
                        | 'DONE'
                        | 'CANCELLED'
                    }
                    children={getChildrenFromTask(task).map((c) => ({
                      name: c.name,
                      emoji: c.emoji_avatar ?? undefined,
                      color: c.emoji_color ?? undefined,
                      imagePath: c.avatar_image_path ?? undefined,
                    }))}
                    members={getMembersFromTask(task).map((m) => ({
                      name: m.display_name ?? m.email ?? 'Member',
                      emoji: m.avatar_emoji ?? undefined,
                      color: m.avatar_color ?? undefined,
                      imagePath: m.avatar_image_path ?? undefined,
                    }))}
                    sourceSubject={getSourceSubject(task)}
                    onToggleComplete={
                      canComplete
                        ? () => void completion.toggle(task)
                        : undefined
                    }
                    onClick={() => setDetailItem({ kind: 'task', data: task })}
                    timezone={userTimezone}
                  />
                );
              })}
            </div>
          </CollapsibleSection>
        )}

        {/* Empty state — "You're all cleared for today!" — on its own, not
            inside a section, once every section has emptied. */}
        {!loading && !error && !hasContent && (
          <div className="flex flex-col items-center justify-center py-20">
            <PartyPopper
              size={36}
              className="text-muted-foreground mb-3"
            />
            <p className="text-xl font-medium text-muted-foreground">
              You&apos;re all cleared for today!
            </p>
          </div>
        )}

        {/* Unscheduled — open tasks the LLM extracted without a due date.
            Surfaced here (not Schedule) so they stay discoverable; tapping
            a card opens the edit modal where the user can assign a date,
            which moves the task into the regular schedule. */}
        {!loading && !error && noDateTasks.length > 0 && (
          <CollapsibleSection
            title="Unscheduled"
            count={noDateTasks.length}
            tone="neutral"
          >
            <div className="flex flex-col gap-2">
              {noDateTasks.map((task) => (
                <TaskCard
                  key={task.id}
                  id={task.id}
                  title={task.title}
                  description={task.notes ?? undefined}
                  location={task.location ?? undefined}
                  link={task.link ?? undefined}
                  status={
                    task.status as 'TODO' | 'IN_PROGRESS' | 'DONE' | 'CANCELLED'
                  }
                  children={getChildrenFromTask(task).map((c) => ({
                    name: c.name,
                    emoji: c.emoji_avatar ?? undefined,
                    color: c.emoji_color ?? undefined,
                    imagePath: c.avatar_image_path ?? undefined,
                  }))}
                  members={getMembersFromTask(task).map((m) => ({
                    name: m.display_name ?? m.email ?? 'Member',
                    emoji: m.avatar_emoji ?? undefined,
                    color: m.avatar_color ?? undefined,
                    imagePath: m.avatar_image_path ?? undefined,
                  }))}
                  sourceSubject={getSourceSubject(task)}
                  noDateLabel="Date TBD"
                  onToggleComplete={
                    canComplete ? () => void completion.toggle(task) : undefined
                  }
                  onClick={() => setDetailItem({ kind: 'task', data: task })}
                  timezone={userTimezone}
                />
              ))}
            </div>
          </CollapsibleSection>
        )}
      </div>

      <MobileSuggestionModal
        open={showMobileModal}
        onClose={handleCloseMobileModal}
      />

      {householdId && (
        <AhaWalkthroughModal
          open={walkthroughOpen}
          onClose={() => setWalkthroughOpen(false)}
          householdId={householdId}
        />
      )}

      <FeedbackModal
        open={feedback.open}
        onClose={feedback.onClose}
      />

      {detailItem && (
        <TaskEventDetailModal
          item={detailItem}
          canEdit={canEdit}
          timezone={userTimezone}
          relatedEventTitle={
            detailItem.kind === 'task' && detailItem.data.related_event_id
              ? events.find((e) => e.id === detailItem.data.related_event_id)
                  ?.title
              : undefined
          }
          onClose={() => setDetailItem(null)}
          onEdit={() => {
            setEditingItem(detailItem);
            setDetailItem(null);
          }}
          onToggleComplete={
            canComplete
              ? (id, status) =>
                  void completion.toggle({
                    id,
                    title: detailItem.data.title,
                    status,
                  })
              : undefined
          }
        />
      )}

      {editingItem && (
        <ItemFormModal
          mode="edit"
          initialType={editingItem.kind}
          initialData={
            editingItem.kind === 'task'
              ? taskToReviewItem(editingItem.data)
              : eventToReviewItem(editingItem.data)
          }
          // YAR-295: was hardcoded `[]`, which hid the "Related Event" field
          // entirely. Exclude the item being edited so an event can't link to
          // itself.
          availableEvents={events
            .filter((e) => e.id !== editingItem.data.id)
            .map((e) => ({ id: e.id, title: e.title }))}
          availableChildren={householdChildren.map((c) => ({
            name: c.name,
            emoji_avatar: c.emoji_avatar,
            emoji_color: c.emoji_color,
            avatar_image_path: c.avatar_image_path,
          }))}
          availableMembers={householdMembers
            .filter((m) => m.users)
            .map((m) => ({
              user_id: m.user_id,
              display_name: m.users!.display_name ?? m.users!.email ?? '',
              email: m.users!.email,
              avatar_emoji: m.users!.avatar_emoji,
              avatar_color: m.users!.avatar_color,
              avatar_image_path: m.users!.avatar_image_path,
            }))}
          initialMemberIds={
            editingItem.kind === 'task'
              ? getMembersFromTask(editingItem.data).map((m) => m.id)
              : getMembersFromEvent(editingItem.data).map((m) => m.id)
          }
          onSubmit={handleEditItem}
          onBack={() => setEditingItem(null)}
          seriesDelete={buildSeriesDelete({
            event: editingItem.kind === 'event' ? editingItem.data : null,
            householdId,
            canDelete,
            onDeleted: async () => {
              setEditingItem(null);
              if (householdId) await reloadAll(householdId);
            },
          })}
          onDelete={
            canDelete
              ? async () => {
                  if (editingItem.kind === 'task') {
                    await scheduleService.deleteTask(editingItem.data.id);
                  } else {
                    await scheduleService.deleteEvent(editingItem.data.id);
                  }
                  setEditingItem(null);
                  if (householdId) await reloadAll(householdId);
                }
              : undefined
          }
        />
      )}
    </div>
  );
}
